import React, { useEffect, useState } from 'react';
import { obtenerCotizacionPublica, responderCotizacionPublica } from '../services/cotizacionesService';
import { estadoInfo } from '../utils/cotizaciones';
import { formatoMoneda } from '../utils/servicios';
import { descargarCotizacionPdf } from '../utils/cotizacionPdf';

// Página PÚBLICA de una cotización (Paso 3). La abre el cliente con el enlace
// secreto (token), SIN cuenta. Mobile-first, con la marca del negocio. Puede
// aprobar/rechazar y descargar un PDF (vía imprimir → guardar como PDF).

const fechaLarga = (f) => f
  ? new Date(f + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })
  : '';

export default function CotizacionPublica({ token }) {
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState('cargando'); // 'cargando' | 'ok' | 'noencontrado'
  const [respondiendo, setRespondiendo] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const descargarPdf = async () => {
    setDescargando(true);
    try { await descargarCotizacionPdf(datos); }
    catch { setAviso('No se pudo generar el PDF. Intenta de nuevo.'); }
    finally { setDescargando(false); }
  };

  useEffect(() => {
    let activo = true;
    obtenerCotizacionPublica(token)
      .then((d) => {
        if (!activo) return;
        if (!d || !d.cotizacion) { setEstado('noencontrado'); return; }
        setDatos(d);
        setEstado('ok');
      })
      .catch(() => { if (activo) setEstado('noencontrado'); });
    return () => { activo = false; };
  }, [token]);

  const responder = async (accion) => {
    setRespondiendo(true);
    setAviso(null);
    try {
      const nuevo = await responderCotizacionPublica(token, accion);
      setDatos((prev) => ({ ...prev, cotizacion: { ...prev.cotizacion, estado: nuevo } }));
    } catch (e) {
      const msg = String(e?.message || '');
      setAviso(msg.includes('NO_DISPONIBLE')
        ? 'Esta cotización ya no está disponible para responder.'
        : 'No se pudo registrar tu respuesta. Intenta de nuevo.');
    } finally {
      setRespondiendo(false);
    }
  };

  const centro = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', padding: '20px', fontFamily: 'Arial, sans-serif', textAlign: 'center' };

  if (estado === 'cargando') {
    return <div style={centro}><p style={{ color: '#6B7280', fontWeight: 700 }}>⏳ Cargando cotización…</p></div>;
  }
  if (estado === 'noencontrado') {
    return (
      <div style={centro}>
        <div style={{ maxWidth: '340px', color: '#4B5563' }}>
          <div style={{ fontSize: '44px' }}>🔍</div>
          <p style={{ margin: '8px 0 0', fontWeight: 700, color: '#111827' }}>No encontramos esta cotización</p>
          <p style={{ margin: '6px 0 0', fontSize: '14px' }}>Revisa que el enlace esté completo, o pídele a la empresa que te lo comparta de nuevo.</p>
        </div>
      </div>
    );
  }

  const { cotizacion: c, items, negocio: n } = datos;
  const moneda = n?.moneda || c.moneda || 'L';
  const fmt = (v) => formatoMoneda(v, moneda);
  const acento = n?.color_principal || '#DB2777';
  const est = estadoInfo(c.estado);
  const puedeResponder = c.estado === 'enviada' || c.estado === 'vista';
  const hayImpuesto = Number(c.impuesto) > 0;
  const hayDescuento = Number(c.descuento) > 0;
  const hayAnticipo = Number(c.anticipo) > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#EEF0F3', fontFamily: 'Arial, sans-serif', padding: '16px 12px', boxSizing: 'border-box' }}>
      <style>{`@media print {
        body { background: #fff !important; }
        .no-print { display: none !important; }
        .hoja { box-shadow: none !important; margin: 0 !important; }
      }`}</style>

      <div className="hoja" style={{ maxWidth: '640px', margin: '0 auto', background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,0.08)' }}>
        {/* Encabezado con la marca del negocio */}
        <div style={{ background: acento, color: '#fff', padding: '22px 22px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {n?.logo_url
              ? <img src={n.logo_url} alt="" style={{ width: '52px', height: '52px', borderRadius: '12px', objectFit: 'cover', background: '#fff' }} />
              : <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 800 }}>{(n?.nombre || 'K').charAt(0).toUpperCase()}</div>}
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>{n?.nombre || 'Cotización'}</h1>
              <p style={{ margin: '2px 0 0', fontSize: '13px', opacity: 0.9 }}>Cotización {c.numero || ''}</p>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {/* Estado + fechas. Los estados internos "Enviada"/"Vista por el cliente"
              no se le muestran al cliente (para él no significan nada); sí se ven
              los que le importan (Aprobada, Rechazada, etc.). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            {!puedeResponder
              ? <span style={{ fontSize: '12.5px', fontWeight: 700, color: est.color, background: est.bg, padding: '4px 12px', borderRadius: '999px' }}>{est.nombre}</span>
              : <span />}
            <div style={{ textAlign: 'right', fontSize: '12.5px', color: '#6B7280' }}>
              {c.fecha_emision && <div>Emitida: {fechaLarga(c.fecha_emision)}</div>}
              {c.fecha_vencimiento && <div>Válida hasta: {fechaLarga(c.fecha_vencimiento)}</div>}
            </div>
          </div>

          {c.cliente_nombre && (
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#374151' }}>
              Para: <strong>{c.cliente_nombre}</strong>
            </p>
          )}

          {/* Renglones */}
          <div style={{ borderTop: '1px solid #F3F4F6' }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '12px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14.5px', color: '#111827', fontWeight: 600 }}>{it.descripcion}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#9CA3AF' }}>
                    {Number(it.cantidad)} × {fmt(it.precio_unitario)}
                    {Number(it.descuento) > 0 ? ` · desc. ${fmt(it.descuento)}` : ''}
                    {Number(it.impuesto_pct) > 0 ? ` · imp. ${Number(it.impuesto_pct)}%` : ''}
                  </p>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: '14.5px', fontWeight: 700, color: '#111827' }}>{fmt(it.total)}</div>
              </div>
            ))}
          </div>

          {/* Totales */}
          <div style={{ marginTop: '14px', fontSize: '14px', color: '#374151' }}>
            <FilaTotal etiqueta="Subtotal" valor={fmt(c.subtotal)} />
            {hayDescuento && <FilaTotal etiqueta="Descuento" valor={`- ${fmt(c.descuento)}`} />}
            {hayImpuesto && <FilaTotal etiqueta="Impuesto" valor={fmt(c.impuesto)} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #E5E7EB', marginTop: '6px' }}>
              <strong style={{ fontSize: '16px', color: '#111827' }}>Total</strong>
              <strong style={{ fontSize: '20px', color: acento }}>{fmt(c.total)}</strong>
            </div>
            {hayAnticipo && (
              <>
                <FilaTotal etiqueta="Anticipo" valor={fmt(c.anticipo)} />
                <FilaTotal etiqueta="Saldo pendiente" valor={fmt(c.saldo)} fuerte />
              </>
            )}
          </div>

          {c.notas && <Bloque titulo="Información adicional" texto={c.notas} />}
          {c.terminos && <Bloque titulo="Términos y condiciones" texto={c.terminos} />}

          {/* Contacto del negocio */}
          {(n?.telefono || n?.instagram) && (
            <p style={{ margin: '18px 0 0', fontSize: '12.5px', color: '#9CA3AF', textAlign: 'center' }}>
              {n?.telefono ? `📞 ${n.telefono}` : ''}{n?.telefono && n?.instagram ? '  ·  ' : ''}{n?.instagram ? `📷 ${n.instagram}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Acciones (no salen en el PDF) */}
      <div className="no-print" style={{ maxWidth: '640px', margin: '16px auto 0' }}>
        {aviso && (
          <p style={{ background: '#FEF2F2', color: '#991B1B', padding: '12px', borderRadius: '10px', fontSize: '13.5px', fontWeight: 600, textAlign: 'center', margin: '0 0 12px' }}>⚠️ {aviso}</p>
        )}

        {c.estado === 'aprobada' && <Banner emoji="✅" texto="¡Aprobaste esta cotización! La empresa fue notificada." color="#047857" bg="#D1FAE5" />}
        {c.estado === 'rechazada' && <Banner emoji="✋" texto="Rechazaste esta cotización." color="#B91C1C" bg="#FEE2E2" />}
        {c.estado === 'convertida' && <Banner emoji="🎉" texto="Esta cotización ya se convirtió en tu compra." color="#065F46" bg="#A7F3D0" />}
        {c.estado === 'cancelada' && <Banner emoji="🚫" texto="Esta cotización fue cancelada por la empresa." color="#6B7280" bg="#F3F4F6" />}
        {c.estado === 'vencida' && <Banner emoji="⌛" texto="Esta cotización venció. Pídele a la empresa una nueva." color="#B45309" bg="#FEF3C7" />}

        {puedeResponder && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <button onClick={() => responder('rechazar')} disabled={respondiendo}
              style={{ padding: '15px', borderRadius: '12px', border: '1.5px solid #FCA5A5', background: '#fff', color: '#DC2626', fontSize: '15px', fontWeight: 700, cursor: respondiendo ? 'wait' : 'pointer' }}>
              Rechazar
            </button>
            <button onClick={() => responder('aprobar')} disabled={respondiendo}
              style={{ padding: '15px', borderRadius: '12px', border: 'none', background: respondiendo ? '#9CA3AF' : '#059669', color: '#fff', fontSize: '15px', fontWeight: 800, cursor: respondiendo ? 'wait' : 'pointer' }}>
              {respondiendo ? '⏳…' : '✓ Aprobar'}
            </button>
          </div>
        )}

        <button onClick={descargarPdf} disabled={descargando}
          style={{ width: '100%', padding: '13px', borderRadius: '12px', border: `1.5px solid ${acento}`, background: '#fff', color: acento, fontSize: '14.5px', fontWeight: 700, cursor: descargando ? 'wait' : 'pointer' }}>
          {descargando ? '⏳ Generando PDF…' : '📄 Descargar PDF'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '11.5px', color: '#9CA3AF', margin: '14px 0 8px' }}>Cotización hecha con Kontrol · by Automatika.IA</p>
      </div>
    </div>
  );
}

function FilaTotal({ etiqueta, valor, fuerte }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: fuerte ? 700 : 400, color: fuerte ? '#111827' : '#6B7280' }}>
      <span>{etiqueta}</span><span>{valor}</span>
    </div>
  );
}

function Bloque({ titulo, texto }) {
  return (
    <div style={{ marginTop: '18px' }}>
      <p style={{ margin: '0 0 4px', fontSize: '12.5px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{titulo}</p>
      <p style={{ margin: 0, fontSize: '13.5px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{texto}</p>
    </div>
  );
}

function Banner({ emoji, texto, color, bg }) {
  return (
    <div style={{ background: bg, color, padding: '14px 16px', borderRadius: '12px', fontSize: '14.5px', fontWeight: 700, textAlign: 'center', marginBottom: '10px' }}>
      {emoji} {texto}
    </div>
  );
}
